import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayrolls } from "@query";
import {
  useWSModalCustom,
  useWSSnackbar,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";

/** Shared props for the payroll-run confirmation modals. Resolves `true` on success. */
type ActionProps = {
  payrollId: string;
  /** Human-readable run number, for the confirmation copy. */
  payrollNumber: string;
} & WSModalCustomProps<boolean>;

/** Presentational confirm shell shared by the finalize/cancel/delete modals. */
const PayrollConfirmModal: React.FC<{
  title: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  isLoading: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}> = props => (
  <WSModal
    actions={{
      buttons: [
        {
          label: props.cancelLabel ?? "Cancel",
          kind: "Secondary",
          type: "button",
          onClick: props.onCancel
        },
        {
          label: props.confirmLabel,
          kind: "Primary",
          destructive: props.destructive,
          loading: props.isLoading,
          onClick: props.onConfirm
        }
      ]
    }}
    size="S"
    title={props.title}
    onClose={props.onCancel}
  >
    <WSList gap="M">
      <WSText color="gray600">{props.children}</WSText>
      <ErrorAlert context="PayrollAction" error={props.error} />
    </WSList>
  </WSModal>
);

const ModalFinalizePayroll: React.FC<ActionProps> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [finalizePayroll, meta] = queriesPayrolls.finalizePayroll.useMutation();

  return (
    <PayrollConfirmModal
      confirmLabel="Finalize"
      error={meta.error}
      isLoading={meta.isLoading}
      title={`Finalize payroll run #${props.payrollNumber}?`}
      onCancel={() => props.onClose(false)}
      onConfirm={() =>
        finalizePayroll(
          { payrollId: props.payrollId },
          {
            onSuccess: () => {
              props.onClose(true);
              openSnackbar({
                icon: { name: "check" },
                message: "Payroll run finalized"
              });
            }
          }
        )
      }
    >
      Once finalized, this payroll run is locked and its funds are scheduled for
      payout. Work logs that aren&apos;t approved yet won&apos;t be included.
    </PayrollConfirmModal>
  );
};

const ModalCancelPayroll: React.FC<ActionProps> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [cancelPayroll, meta] = queriesPayrolls.cancelPayroll.useMutation();

  return (
    <PayrollConfirmModal
      cancelLabel="Keep run"
      confirmLabel="Cancel payroll run"
      destructive
      error={meta.error}
      isLoading={meta.isLoading}
      title={`Cancel payroll run #${props.payrollNumber}?`}
      onCancel={() => props.onClose(false)}
      onConfirm={() =>
        cancelPayroll(
          { payrollId: props.payrollId },
          {
            onSuccess: () => {
              props.onClose(true);
              openSnackbar({
                icon: { name: "check" },
                message: "Payroll run cancelled"
              });
            }
          }
        )
      }
    >
      Cancelling stops this payroll run — workers won&apos;t be paid for it.
      This can&apos;t be undone.
    </PayrollConfirmModal>
  );
};

const ModalDeletePayroll: React.FC<ActionProps> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [deletePayroll, meta] = queriesPayrolls.deletePayroll.useMutation();

  return (
    <PayrollConfirmModal
      confirmLabel="Delete"
      destructive
      error={meta.error}
      isLoading={meta.isLoading}
      title={`Delete payroll run #${props.payrollNumber}?`}
      onCancel={() => props.onClose(false)}
      onConfirm={() =>
        deletePayroll(
          { payrollId: props.payrollId },
          {
            onSuccess: () => {
              props.onClose(true);
              openSnackbar({
                icon: { name: "check" },
                message: "Payroll run deleted"
              });
            }
          }
        )
      }
    >
      This permanently removes the payroll run. This can&apos;t be undone.
    </PayrollConfirmModal>
  );
};

export const useModalFinalizePayroll = () =>
  useWSModalCustom(ModalFinalizePayroll);
export const useModalCancelPayroll = () => useWSModalCustom(ModalCancelPayroll);
export const useModalDeletePayroll = () => useWSModalCustom(ModalDeletePayroll);
